/*
 * @providesModule PlayerLevelsApiController
 * 
 */

import CurrentUserStore from 'CurrentUserStore';
import {NativeModules} from 'react-native';
import NetClient from 'NetClient';
import RESTStore from 'RESTStore';
import SKZPlayerLevel from 'SKZPlayerLevel';

const SKZAPIManager = NativeModules.SKZAPIManager;

const PAGE_SIZE = 15;

export type ShortListResponse = {gameLevel: SKZPlayerLevel, skillzLevel: SKZPlayerLevel};

class PlayerLevelsApiController {
  PAGE_SIZE = PAGE_SIZE;
  currentOffset: number = 0;
  _userId: string = null;

  getShortList(useCache?: boolean): Promise {
    return new Promise((resolve: (response: ShortListResponse)=>void, reject:(error: unknown)=>void) => {
      CurrentUserStore.getCurrentUserId().then((userId: string) => {
        this.getShortListForUser(userId, useCache).then(resolve).catch(reject);
      });
    });
  }

  getShortListForUser(userId: string | number, useCache?: boolean, skipProcessing?: boolean): Promise {
    this._resetPagination();
    return new Promise((resolve: (response: ShortListResponse)=>void, reject:(error: unknown)=>void) => {
      const getResponse = useCache ? RESTStore.getAnyResponse.bind(RESTStore) : RESTStore.getServerResponse.bind(RESTStore);

      // Put query param directly into the URL so that the cache string includes it- this prevents
      // the responses getLongList or getNextPage from overwriting this response in the cache
      getResponse(`users/${userId}/levels?limit=2`, null, SKZAPIManager.OnError.failSilently, (response: unknown, error: unknown) => {
        if (response) {
          skipProcessing ? resolve(response) : resolve(this._processShortListResponse(response));
        } else {
          reject(error);
        }
      });
    });
  }

  getLongList(userId: string): Promise {
    this._resetPagination();
    this._userId = userId;

    return this._getPage();
  }

  markLevelAsSeen(level: SKZPlayerLevel): Promise {
    return new Promise((resolve: ()=>void, reject:(error: unknown)=>void) => {
      if (!level.isSkillzLevel() && !level.isCurrentGameLevel()) {
        reject(`PlayerLevelsApiController: cannot mark level for ${level.game_name} as seen from current game`);
        return;
      }

      const body = {
        system_wide: level.isSkillzLevel(),
        level: level.current_level,
      };
      CurrentUserStore.getCurrentUserId().then((userId: string) => {
        NetClient.patchURL(`users/${userId}/levels/viewed`, body, SKZAPIManager.OnError.failSilently,
          (error: Object, payload: Object) => {
            if (error) {
              reject(error);
            } else {
              this._updateLevelCache(level, () => {
                resolve();
              });
            }
          }
        );
      });
    });
  }

  getNextPage(): Promise {
    console.debug('PlayerLevelsApiController: get next page of player levels');
    return new Promise((resolve: (response: Array<Object>)=>void, reject:(error: unknown)=>void) => {
      if (!this.currentOffset || !this._userId) {
        reject('PlayerLevelsApiController: attempting to get next page before getting first page');
      }

      this._getPage(this.currentOffset).then((result: unknown) => {
        resolve(result);
      }).catch((error: unknown) => {
        reject(error);
      });
    });
  }

  _processShortListResponse(response: Array<Object>): ShortListResponse {
    const formattedLevels = response.map((level: Object): SKZPlayerLevel => {return new SKZPlayerLevel(level);});
    let gameLevel = null;
    let skillzLevel = null;
    formattedLevels.forEach((level: SKZPlayerLevel) => {
      if (level.isCurrentGameLevel()) {
        gameLevel = level;
      } else if (level.isSkillzLevel()) {
        skillzLevel = level;
      }
    });

    return { skillzLevel, gameLevel };
  }

  _getPage(offset: number): Promise {
    return new Promise((resolve: (response: Object)=>void, reject:(error: unknown)=>void) => {
      const params = {limit: PAGE_SIZE};
      if (offset) {
        params.offset = offset;
      }
      RESTStore.getServerResponse(`users/${this._userId}/levels`, params, SKZAPIManager.OnError.failSilently, (response: unknown, error: unknown) => {
        if (response) {
          this._savePageDownId(response);
          resolve(response);
        } else {
          reject(error);
        }
      });
    });
  }

  _updateLevelCache(level: SKZPlayerLevel, completion: ()=>void) {
    level.last_seen_level = level.current_level;

    CurrentUserStore.getCurrentUserId().then((userId: string) => {
      RESTStore.getCachedResponse(`users/${this._userId}/levels?limit=2`, (response: unknown, error: unknown) => {
        if (error || !response) {
          completion();
          return;
        }
        response.forEach((item: Object) => {
          if (item.id == level.id) {
            item.last_seen_level = level.current_level;
          }
        });

        RESTStore.updateLocalStorage(`users/${this._userId}/levels?limit=2`, response).finally(() => {
          completion();
        });
      });
    });
  }

  _resetPagination() {
    this.currentOffset = 0;
    this._userId = null;
  }

  _savePageDownId(response: Array<Object>) {
    this.currentOffset = Math.min(response.length, PAGE_SIZE) + this.currentOffset;
  }
}

export default new PlayerLevelsApiController();
